import { useState, useMemo, useRef, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchBdData, fetchBdSummary, fetchBdBriefs, fetchBdInboundLeads, fetchBdBriefsDoc } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import BdSettingsDialog from "@/pages/client-tabs/BdSettingsDialog";
import OutboundSkyleadSection from "@/pages/client-tabs/OutboundSkyleadSection";
import ConversacionesOutboundSection from "@/pages/client-tabs/ConversacionesOutboundSection";
import ReporteOutreachSection from "@/pages/client-tabs/ReporteOutreachSection";
import PorFueraSection from "@/pages/client-tabs/PorFueraSection";
import MonthlyMetricsTable from "@/pages/client-tabs/MonthlyMetricsTable";
import {
  Settings, Briefcase, CalendarDays, Activity, ArrowDownToLine, ArrowUpFromLine,
  Users, Globe2, AlertCircle, Search, Building2, Mail, MapPin, TrendingUp, TrendingDown, Minus,
  Clock, FileText, ExternalLink, Phone, Linkedin, Video, MessageSquare, UserPlus,
  Table as TableIcon,
} from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

export default function BdTab({ clientId }) {
  const { data: cfgDoc, isLoading } = useQuery({ queryKey: ["bd-data", clientId], queryFn: () => fetchBdData(clientId) });
  const [settingsOpen, setSettingsOpen] = useState(false);
  const cfg = cfgDoc?.config || {};
  const hasSheet = !!(cfg.has_sheet || cfg.sheet_id);
  const hasPorFuera = !!(cfg.has_por_fuera || cfg.por_fuera_sheet_url);

  if (isLoading) return <div className="h-72 rounded-md skeleton" />;

  return (
    <div className="space-y-5" data-testid="bd-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Desarrollo de Negocios</p>
          <h2 className="font-display text-lg font-semibold text-slate-900">Pipeline · Reuniones · Outreach</h2>
        </div>
        <Button variant="outline" size="sm" onClick={() => setSettingsOpen(true)} data-testid="bd-config-btn">
          <Settings className="h-3.5 w-3.5 mr-1.5" /> Configurar compromiso
        </Button>
      </div>

      {!hasSheet && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-4 text-xs text-amber-900 flex items-start gap-3" data-testid="bd-no-sheet">
          <AlertCircle className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold mb-1">Sin Google Sheet configurado.</div>
            <p>Hacé clic en <span className="font-semibold">Configurar compromiso</span> y pegá el link del Sheet del cliente para que aparezcan automáticamente Reuniones, Outbound e Inbound.</p>
          </div>
        </div>
      )}

      <Tabs defaultValue="reporte" className="w-full">
        <TabsList className="bg-slate-100 p-1 rounded-md inline-flex gap-1">
          <TabsTrigger value="reporte" data-testid="bd-tab-reporte" className="data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-600 px-3 py-1.5 rounded-sm text-xs font-medium gap-1.5"><FileText className="h-3.5 w-3.5" /> Reporte semanal</TabsTrigger>
          <TabsTrigger value="tablero" data-testid="bd-tab-tablero" className="data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-600 px-3 py-1.5 rounded-sm text-xs font-medium gap-1.5"><TableIcon className="h-3.5 w-3.5" /> Tablero mensual</TabsTrigger>
          <TabsTrigger value="reuniones" data-testid="bd-tab-reuniones" className="data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-600 px-3 py-1.5 rounded-sm text-xs font-medium gap-1.5"><CalendarDays className="h-3.5 w-3.5" /> Reuniones</TabsTrigger>
          <TabsTrigger value="outbound" data-testid="bd-tab-outbound" className="data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-600 px-3 py-1.5 rounded-sm text-xs font-medium gap-1.5"><ArrowUpFromLine className="h-3.5 w-3.5" /> Outbound</TabsTrigger>
          <TabsTrigger value="por-fuera" data-testid="bd-tab-por-fuera" className="data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-600 px-3 py-1.5 rounded-sm text-xs font-medium gap-1.5"><UserPlus className="h-3.5 w-3.5" /> Por Fuera</TabsTrigger>
          <TabsTrigger value="conv-outbound" data-testid="bd-tab-conv-outbound" className="data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-600 px-3 py-1.5 rounded-sm text-xs font-medium gap-1.5"><MessageSquare className="h-3.5 w-3.5" /> Conversaciones outbound</TabsTrigger>
          <TabsTrigger value="inbound" data-testid="bd-tab-inbound" className="data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-600 px-3 py-1.5 rounded-sm text-xs font-medium gap-1.5"><ArrowDownToLine className="h-3.5 w-3.5" /> Inbound</TabsTrigger>
          <TabsTrigger value="compromiso" data-testid="bd-tab-compromiso" className="data-[state=active]:bg-white data-[state=active]:shadow-sm data-[state=active]:text-slate-900 text-slate-600 px-3 py-1.5 rounded-sm text-xs font-medium gap-1.5"><Settings className="h-3.5 w-3.5" /> Compromiso</TabsTrigger>
        </TabsList>

        <TabsContent value="compromiso" className="mt-6"><CompromisoSection cfg={cfg} /></TabsContent>
        <TabsContent value="tablero" className="mt-6">
          <MonthlyMetricsTable
            clientId={clientId}
            endpoint={`/clients/${clientId}/bd/monthly-metrics`}
            title="Tablero mensual BD"
            subtitle="Outreach LinkedIn por perfil + reuniones de la planilla de briefs, mes a mes. Solo muestra meses cerrados; el mes en curso aparece cuando termina."
          />
        </TabsContent>
        <TabsContent value="reporte" className="mt-6"><ReporteSemanalSection clientId={clientId} hasSheet={hasSheet} cfg={cfg} hasOutreach={(cfg.skylead_accounts || []).length > 0} hasPorFuera={hasPorFuera} /></TabsContent>
        <TabsContent value="reuniones" className="mt-6"><ReunionesSection clientId={clientId} hasSheet={hasSheet} cfg={cfg} /></TabsContent>
        <TabsContent value="outbound" className="mt-6"><OutboundSection clientId={clientId} hasSheet={hasSheet} skyleadAccounts={cfg.skylead_accounts || []} /></TabsContent>
        <TabsContent value="por-fuera" className="mt-6"><PorFueraSection clientId={clientId} hasPorFuera={hasPorFuera} /></TabsContent>
        <TabsContent value="conv-outbound" className="mt-6"><ConversacionesOutboundSection clientId={clientId} skyleadAccounts={cfg.skylead_accounts || []} hasPorFuera={hasPorFuera} /></TabsContent>
        <TabsContent value="inbound" className="mt-6"><InboundSection clientId={clientId} hasSheet={hasSheet} /></TabsContent>
      </Tabs>

      <BdSettingsDialog clientId={clientId} open={settingsOpen} onOpenChange={setSettingsOpen} current={cfgDoc} />
    </div>
  );
}

/* ============== Compromiso ============== */
function CompromisoSection({ cfg }) {
  const perfiles = cfg.perfiles || [];
  const com = cfg.commitments || {};
  const hasAny = perfiles.length || Object.keys(com).length;

  if (!hasAny) {
    return <div className="text-xs text-slate-400 italic">Sin compromiso definido. Hacé clic en <span className="font-semibold">Configurar compromiso</span> para empezar.</div>;
  }

  return (
    <div className="space-y-5" data-testid="bd-compromiso">
      {/* Período */}
      {(cfg.period_start || cfg.period_end) && (
        <div className="text-[11px] font-mono text-slate-500">Período: {cfg.period_start || "—"} → {cfg.period_end || "—"}</div>
      )}

      {/* Perfiles */}
      {perfiles.length > 0 && (
        <section className="bg-white border border-slate-200 rounded-md p-5">
          <h3 className="font-display text-sm font-semibold text-slate-900 mb-3 flex items-center gap-1.5"><Users className="h-4 w-4 text-indigo-600" /> Perfiles activos · objetivos por perfil</h3>
          <div className="overflow-x-auto -mx-5">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-y border-slate-200">
                <tr className="text-left text-slate-500 uppercase tracking-wider text-[10px]">
                  <th className="px-3 py-2 font-semibold">Nombre</th>
                  <th className="px-2 py-2 font-semibold">Rol</th>
                  <th className="px-2 py-2 font-semibold">Red</th>
                  <th className="px-2 py-2 font-semibold text-right">Prospectos</th>
                  <th className="px-2 py-2 font-semibold text-right">Invitaciones</th>
                  <th className="px-2 py-2 font-semibold text-right">Mensajes</th>
                  <th className="px-2 py-2 font-semibold">Mercados</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {perfiles.map((p, i) => (
                  <tr key={i} className="hover:bg-slate-50 align-top">
                    <td className="px-3 py-2 font-medium text-slate-800">{p.nombre || <span className="text-slate-400">(sin nombre)</span>}</td>
                    <td className="px-2 py-2 text-slate-600">{p.rol}</td>
                    <td className="px-2 py-2"><span className="text-[10px] bg-indigo-50 border border-indigo-200 text-indigo-700 px-1.5 py-0.5 rounded-sm font-medium whitespace-nowrap">{p.red}</span></td>
                    <td className="px-2 py-2 text-right font-mono tabular-nums text-slate-900">{(p.prospectos_objetivo || 0).toLocaleString()}</td>
                    <td className="px-2 py-2 text-right font-mono tabular-nums text-slate-900">{(p.invitaciones_objetivo || 0).toLocaleString()}</td>
                    <td className="px-2 py-2 text-right font-mono tabular-nums text-slate-900">{(p.mensajes_objetivo || 0).toLocaleString()}</td>
                    <td className="px-2 py-2">
                      <div className="flex flex-wrap gap-1">
                        {(p.mercados || []).map((m) => <span key={m} className="text-[10px] bg-emerald-50 border border-emerald-200 text-emerald-700 px-1.5 py-0.5 rounded-sm font-medium">{m}</span>)}
                        {(!p.mercados || p.mercados.length === 0) && <span className="text-[10px] text-slate-400 italic">—</span>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Commitments globales del período */}
      {Object.keys(com).length > 0 && (
        <section className="bg-white border border-slate-200 rounded-md p-5">
          <h3 className="font-display text-sm font-semibold text-slate-900 mb-3 flex items-center gap-1.5"><TrendingUp className="h-4 w-4 text-violet-600" /> Compromisos del período (global)</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2.5">
            {Object.entries(com).filter(([, v]) => v > 0).map(([k, v]) => (
              <div key={k} className="bg-slate-50 border border-slate-200 rounded-md p-3">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-0.5">{labelize(k)}</div>
                <div className="font-mono text-lg font-semibold tabular-nums text-slate-900">{v.toLocaleString()}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {cfg.notes && (
        <section className="bg-amber-50 border-l-2 border-amber-300 px-4 py-3 rounded-r-md">
          <h4 className="text-[10px] uppercase tracking-wider text-amber-800 font-semibold mb-1">Notas</h4>
          <p className="text-xs text-amber-900 leading-relaxed whitespace-pre-line">{cfg.notes}</p>
        </section>
      )}
    </div>
  );
}

function labelize(k) {
  return k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/* ============== Reuniones ============== */
const MESES_ORDER = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const MESES_SHORT = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
// English month names (full + short + Spanish typos) → Spanish canonical.
// Used to normalise "July"/"August"/"Agost"/etc. from Werelo's sheet so
// they line up with MESES_ORDER for grouping and sorting.
const MES_ALIASES = {
  january: "Enero", jan: "Enero", ene: "Enero", enero: "Enero",
  february: "Febrero", feb: "Febrero", febrero: "Febrero",
  march: "Marzo", mar: "Marzo", marzo: "Marzo",
  april: "Abril", apr: "Abril", abr: "Abril", abril: "Abril",
  may: "Mayo", mayo: "Mayo",
  june: "Junio", jun: "Junio", junio: "Junio",
  july: "Julio", jul: "Julio", julio: "Julio",
  august: "Agosto", aug: "Agosto", agost: "Agosto", ago: "Agosto", agosto: "Agosto",
  september: "Septiembre", sept: "Septiembre", sep: "Septiembre", septiembre: "Septiembre",
  october: "Octubre", oct: "Octubre", octubre: "Octubre",
  november: "Noviembre", nov: "Noviembre", noviembre: "Noviembre",
  december: "Diciembre", dec: "Diciembre", dic: "Diciembre", diciembre: "Diciembre",
};
function _normalizeMonth(s) {
  let key = String(s || "").trim().toLowerCase();
  if (!key) return s || "";
  // Strip trailing year tokens some sheets embed in the month label
  // ("septiembre 26" → "septiembre") so they group with the plain month.
  key = key.replace(/\s+'?\d{2,4}$/, "").trim();
  return MES_ALIASES[key] || (s || "").trim();
}

/* Briefs do not carry an explicit year on every row (only `Mes` + a project-wide
 * incrementing `Sem`). To plot meetings chronologically across year boundaries
 * we determine the year **once per month** (not per brief) so duplicates inside
 * the same week don't trigger spurious year bumps. Algorithm:
 *   1. Scan every brief looking for an explicit 4-digit year in any date field.
 *      Build `monthYear[Mes] = year`. First occurrence wins.
 *   2. Sort all months that appear in the briefs by their minimum `Sem`
 *      (the timeline of the project).
 *   3. Walk that ordered list and fill in missing years: bump the year by 1
 *      only when the month index drops vs. the previous known month
 *      (e.g. Diciembre → Enero).
 * Returns the briefs decorated with `_year` and `_mesIdx`.
 */
function _assignBriefYears(briefs) {
  if (!briefs?.length) return briefs || [];
  const extractYear = (s) => {
    if (!s) return null;
    const m = String(s).match(/\b(20\d{2})\b/);
    return m ? +m[1] : null;
  };

  // 1) Year for any month that has at least one row carrying a year.
  //    Prefer the explicit `Año` column when the sheet provides it (Bridar
  //    et al.). Fall back to scanning the date columns as a last resort.
  //    Month names are normalised (July → Julio, etc.) so English sheets
  //    (Werelo) line up with MESES_ORDER.
  const monthYear = {};
  for (const b of briefs) {
    const mes = _normalizeMonth(b.Mes);
    if (!mes || monthYear[mes]) continue;
    const explicitYear = extractYear(b["Año"]) || extractYear(b["Anio"]) || extractYear(b["Ano"]);
    const y = explicitYear || extractYear(b["Fecha de inicio de contacto"]) || extractYear(b["Fecha Final"]);
    if (y) monthYear[mes] = y;
  }

  // 2) Order months by minimum Sem (cronología real del proyecto)
  const monthMinSem = {};
  for (const b of briefs) {
    const mes = _normalizeMonth(b.Mes);
    const sem = Number(b.Sem) || 0;
    if (!mes) continue;
    if (!(mes in monthMinSem) || sem < monthMinSem[mes]) monthMinSem[mes] = sem;
  }
  const orderedMonths = Object.keys(monthMinSem)
    .map((m) => ({ mes: m, idx: MESES_ORDER.indexOf(m), sem: monthMinSem[m] }))
    .filter((x) => x.idx >= 0)
    .sort((a, b) => a.sem - b.sem);

  // 3) Fill in missing years by walking chronologically. Bump only on a real
  //    Dic → Ene type wrap.
  let prevYear = null;
  let prevIdx = -1;
  for (const m of orderedMonths) {
    if (monthYear[m.mes]) {
      prevYear = monthYear[m.mes];
      prevIdx = m.idx;
      continue;
    }
    if (prevYear == null) {
      monthYear[m.mes] = new Date().getFullYear();
    } else {
      monthYear[m.mes] = m.idx < prevIdx ? prevYear + 1 : prevYear;
    }
    prevYear = monthYear[m.mes];
    prevIdx = m.idx;
  }

  return briefs.map((b) => {
    const mes = _normalizeMonth(b.Mes);
    // Prefer the explicit `Año` column on the row itself so briefs with the
    // same month across years (e.g. Sep '25 and Sep '26) don't collapse.
    const rowYear = extractYear(b["Año"]) || extractYear(b["Anio"]) || extractYear(b["Ano"]);
    return { ...b, _year: rowYear || monthYear[mes] || new Date().getFullYear(), _mesIdx: MESES_ORDER.indexOf(mes) };
  });
}

function parseBriefDate(s) {
  if (!s) return null;
  // formats seen: "08/04/2026 15:00", "46154.54167" (excel serial), "22:04/2026 12:00" (typo)
  if (/^\d+(\.\d+)?$/.test(s)) {
    // Excel serial date → ms since 1899-12-30
    const ms = (parseFloat(s) - 25569) * 86400 * 1000;
    return new Date(ms);
  }
  const m = s.match(/(\d{1,2})[\/:](\d{1,2})[\/:](\d{4})\s*(\d{1,2})?:?(\d{2})?/);
  if (m) {
    const [_, d, mo, y, h, mi] = m;
    return new Date(Number(y), Number(mo) - 1, Number(d), Number(h || 0), Number(mi || 0));
  }
  const dt = new Date(s);
  return isNaN(dt) ? null : dt;
}

function ReunionesSection({ clientId, hasSheet, cfg = {} }) {
  const { data, isLoading } = useQuery({ queryKey: ["bd-briefs", clientId], queryFn: () => fetchBdBriefs(clientId), enabled: hasSheet });
  const docQ = useQuery({ queryKey: ["bd-briefs-doc", clientId], queryFn: () => fetchBdBriefsDoc(clientId), enabled: hasSheet, staleTime: 5 * 60 * 1000 });
  const summaryQ = useQuery({ queryKey: ["bd-summary", clientId], queryFn: () => fetchBdSummary(clientId), enabled: hasSheet });
  const [search, setSearch] = useState("");
  const [stateFilter, setStateFilter] = useState("Todos");
  const [openedBrief, setOpenedBrief] = useState(null); // {empresa, ...}
  const briefs = data?.briefs || [];
  const docBriefs = docQ.data?.briefs || [];

  const normalizeEmpresa = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^\w\s]/g, "").replace(/\s+/g, " ").trim();
  const findDocBrief = (empresa) => {
    if (!empresa || !docBriefs.length) return null;
    const key = normalizeEmpresa(empresa);
    if (!key) return null;
    // 1. Exact match on either empresa_raw or nombre_empresa
    let m = docBriefs.find((d) => {
      const a = normalizeEmpresa(d.empresa_raw);
      const b = normalizeEmpresa(d.nombre_empresa);
      return (a && a === key) || (b && b === key);
    });
    if (m) return m;
    // 2. Partial match — require a MEANINGFUL overlap (min 4 chars). Both
    //    sides must be non-empty to avoid the "" pitfall where key.includes("")
    //    is always true and would return the first brief regardless.
    if (key.length < 4) return null;
    m = docBriefs.find((d) => {
      const a = normalizeEmpresa(d.empresa_raw);
      const b = normalizeEmpresa(d.nombre_empresa);
      const okA = a && a.length >= 4 && (a.includes(key) || key.includes(a));
      const okB = b && b.length >= 4 && (b.includes(key) || key.includes(b));
      return okA || okB;
    });
    return m || null;
  };
  const openBrief = (empresa) => {
    const docBrief = findDocBrief(empresa);
    setOpenedBrief({ empresa, doc: docBrief, sheetRow: briefs.find((b) => b.Empresa === empresa) });
  };

  // Próximas: solo estado Calendarizada / Scheduled (no incluye reprogramar/esperando)
  const proximas = useMemo(() => {
    return briefs
      .filter((b) => /calendarizad|scheduled/i.test(b.ESTADO || ""))
      .map((b) => ({ ...b, _date: parseBriefDate(b["Fecha Final"]) }))
      .sort((a, b) => (a._date?.getTime() || Infinity) - (b._date?.getTime() || Infinity));
  }, [briefs]);

  // Estados que cuentan como "alcanzada" (Realizada + Calendarizada + Pide Cotización)
  // Incluye variantes en inglés (Werelo · Estado de Situación): "Meet Done",
  // "Scheduled", "Quotation Request". Sin esto, meetings de Werelo no se cuentan.
  const ALCANZADA_RE = /realizada|calendarizad|pide\s*cotiz|cotizaci|meet\s*done|meeting\s*done|scheduled|quotation\s*request|quote\s*request/i;
  const REPROGRAMADA_RE = /reprogramar|reschedul/i;
  const POR_CERRAR_RE = /esperando|coordinando|por cerrar|confirmation\s*waiting|coordinating/i;
  const GANADA_RE = /ganada|ganado|\bwon\b/i;

  const alcanzadas = useMemo(() => briefs.filter((b) => ALCANZADA_RE.test(b.ESTADO || "")), [briefs]);
  const reprogramadas = useMemo(() => briefs.filter((b) => REPROGRAMADA_RE.test(b.ESTADO || "")), [briefs]);
  const porCerrar = useMemo(() => briefs.filter((b) => POR_CERRAR_RE.test(b.ESTADO || "")), [briefs]);
  const ganadas = useMemo(() => briefs.filter((b) => GANADA_RE.test((b["STAGE PIPELINE"] || "") + " " + (b.ESTADO || ""))), [briefs]);
  const pedidosCotizacion = useMemo(() => briefs.filter((b) => /pide\s*cotiz|cotizaci|quotation\s*request|quote\s*request/i.test((b.ESTADO || "") + " " + (b["STAGE PIPELINE"] || ""))), [briefs]);

  // Reuniones por mes (Realizada + Calendarizada + Pide Cotización)
  // Aware of year boundaries: groups by (year, monthIdx) and sorts chronologically.
  const byMonth = useMemo(() => {
    const decorated = _assignBriefYears(alcanzadas);
    const counts = new Map(); // key = `${year}-${idx}` → { year, idx, value }
    decorated.forEach((b) => {
      if (b._mesIdx < 0) return;
      const k = `${b._year}-${b._mesIdx}`;
      const prev = counts.get(k) || { year: b._year, idx: b._mesIdx, value: 0 };
      prev.value += 1;
      counts.set(k, prev);
    });
    return Array.from(counts.values())
      .sort((a, b) => (a.year - b.year) || (a.idx - b.idx))
      .map(({ year, idx, value }) => ({
        mes: MESES_ORDER[idx],
        year,
        label: `${MESES_SHORT[idx]} '${String(year).slice(-2)}`,
        value,
      }));
  }, [alcanzadas]);

  // Reuniones por campaña (Realizada + Calendarizada + Pide Cotización)
  const byCampaign = useMemo(() => {
    const counts = {};
    alcanzadas.forEach((b) => {
      const c = (b.Campaña || "Sin campaña").trim();
      counts[c] = (counts[c] || 0) + 1;
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]).map(([camp, val]) => ({ campaign: camp, value: val }));
  }, [alcanzadas]);

  const states = useMemo(() => Array.from(new Set(briefs.map((b) => b.ESTADO).filter(Boolean))).sort(), [briefs]);

  // Decorate every brief with _year / _mesIdx so we can filter + sort by month.
  const briefsWithYear = useMemo(() => _assignBriefYears(briefs), [briefs]);

  // Chronological "Mes Año" options, most-recent first.
  const monthOptions = useMemo(() => {
    const seen = new Map(); // key `${year}-${idx}` → { year, idx, label }
    briefsWithYear.forEach((b) => {
      if (b._mesIdx < 0 || !b._year) return;
      const k = `${b._year}-${b._mesIdx}`;
      if (!seen.has(k)) seen.set(k, { year: b._year, idx: b._mesIdx, key: k, label: `${MESES_SHORT[b._mesIdx]} '${String(b._year).slice(-2)}` });
    });
    return Array.from(seen.values()).sort((a, b) => (b.year - a.year) || (b.idx - a.idx));
  }, [briefsWithYear]);

  const [mesFilter, setMesFilter] = useState("Todos");

  const filtered = useMemo(() => {
    const rows = briefsWithYear.filter((b) => {
      if (stateFilter !== "Todos" && b.ESTADO !== stateFilter) return false;
      if (mesFilter !== "Todos") {
        const k = `${b._year}-${b._mesIdx}`;
        if (k !== mesFilter) return false;
      }
      if (search && !`${b.Empresa} ${b.Participante} ${b.Comentarios}`.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
    // Most-recent first: sort by (year, mesIdx, parsed day) desc.
    return rows.slice().sort((a, b) => {
      const yA = a._year || 0, yB = b._year || 0;
      if (yA !== yB) return yB - yA;
      const mA = a._mesIdx ?? -1, mB = b._mesIdx ?? -1;
      if (mA !== mB) return mB - mA;
      const dA = parseBriefDate(a["Fecha Final"])?.getTime() || 0;
      const dB = parseBriefDate(b["Fecha Final"])?.getTime() || 0;
      return dB - dA;
    });
  }, [briefsWithYear, search, stateFilter, mesFilter]);

  // ── Estado del proyecto ──
  const projectStatus = useMemo(() => {
    const gral = summaryQ.data?.gral_numbers || {};
    const kickOff = (gral.kick_off_date && parseBriefDate(gral.kick_off_date)) || parseBriefDate(cfg.kick_off);
    const inicioOutbound = (gral.outbound_start_date && parseBriefDate(gral.outbound_start_date)) || parseBriefDate(cfg.inicio_outbound);
    const finalizacion = (gral.end_date && parseBriefDate(gral.end_date)) || parseBriefDate(cfg.finalizacion);
    const objetivo = Number(gral.meeting_goals ?? cfg.objetivo_reuniones ?? 0);
    const mesesAdic = Number(gral.adicional_month ?? cfg.meses_adicionales ?? 0);
    const today = new Date();

    const businessDays = (from, to) => {
      if (!from || !to) return 0;
      let count = 0;
      const cur = new Date(from);
      cur.setHours(0, 0, 0, 0);
      const end = new Date(to);
      end.setHours(0, 0, 0, 0);
      while (cur <= end) {
        const d = cur.getDay();
        if (d !== 0 && d !== 6) count++;
        cur.setDate(cur.getDate() + 1);
      }
      return count;
    };

    const totalDays = businessDays(inicioOutbound || kickOff, finalizacion);
    const elapsed = businessDays(inicioOutbound || kickOff, today);
    const remaining = Math.max(0, businessDays(today, finalizacion));
    const reusEsperadas = totalDays > 0 ? (objetivo * elapsed) / totalDays : 0;
    const proyectadas = elapsed > 0 ? Math.round((alcanzadas.length / elapsed) * totalDays) : 0;

    let semaforo = { label: "—", tone: "slate" };
    const gralLight = (gral.traffic_light || "").toString().toLowerCase().trim();
    if (gralLight) {
      if (/muy bien|excellent|on track/.test(gralLight)) semaforo = { label: "Muy bien", tone: "emerald" };
      else if (/bien|in progress|good/.test(gralLight)) semaforo = { label: "En curso", tone: "lime" };
      else if (/riesgo|at risk|warning/.test(gralLight)) semaforo = { label: "Riesgo", tone: "amber" };
      else if (/crítico|critico|critical|off track/.test(gralLight)) semaforo = { label: "Crítico", tone: "rose" };
      else semaforo = { label: gral.traffic_light, tone: "slate" };
    } else if (objetivo > 0 && reusEsperadas > 0) {
      const ratio = alcanzadas.length / reusEsperadas;
      if (ratio >= 1) semaforo = { label: "Muy bien", tone: "emerald" };
      else if (ratio >= 0.8) semaforo = { label: "Bien", tone: "lime" };
      else if (ratio >= 0.6) semaforo = { label: "Riesgo", tone: "amber" };
      else semaforo = { label: "Crítico", tone: "rose" };
    }

    return {
      kickOff: kickOff ? kickOff.toLocaleDateString("es-AR") : null,
      inicioOutbound: inicioOutbound ? inicioOutbound.toLocaleDateString("es-AR") : null,
      finalizacion: finalizacion ? finalizacion.toLocaleDateString("es-AR") : null,
      mesesAdic,
      objetivo,
      reprogramadas: reprogramadas.length,
      alcanzadas: alcanzadas.length,
      pedidosCotizacion: pedidosCotizacion.length,
      porCerrar: porCerrar.length,
      proyectadas,
      diasRestantes: remaining,
      reusEsperadas: Math.round(reusEsperadas * 10) / 10,
      ganadas: ganadas.length,
      semaforo,
      hasConfig: !!(kickOff || finalizacion || objetivo),
    };
  }, [cfg, summaryQ.data, alcanzadas, reprogramadas, pedidosCotizacion, porCerrar, ganadas]);

  if (!hasSheet) return <EmptyMsg msg="Configurá el Google Sheet primero." />;
  if (isLoading) return <div className="h-72 rounded-md skeleton" />;

  const realizadas = briefs.filter((b) => /realizada|meet\s*done|meeting\s*done/i.test(b.ESTADO || "")).length;
  const calendarizadas = briefs.filter((b) => /calendarizad|scheduled/i.test(b.ESTADO || "")).length;
  const pideCoti = briefs.filter((b) => /pide\s*cotiz|cotizaci|quotation\s*request|quote\s*request/i.test(b.ESTADO || "")).length;
  const reprogramar = briefs.filter((b) => /reprogramar|reschedul/i.test(b.ESTADO || "")).length;
  const noAvanza = briefs.filter((b) => /no avanza|cancelada|no\s*progress/i.test(b.ESTADO || "")).length;
  const totalCount = byMonth.reduce((acc, x) => acc + x.value, 0);
  const totalCampaignCount = byCampaign.reduce((acc, x) => acc + x.value, 0);

  return (
    <div className="space-y-5" data-testid="bd-reuniones">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KpiBlock label="Realizadas" value={realizadas} tone="emerald" icon={CalendarDays} />
        <KpiBlock label="Calendarizadas" value={calendarizadas} tone="sky" icon={CalendarDays} />
        <KpiBlock label="Pide cotización" value={pideCoti} tone="indigo" icon={FileText} />
        <KpiBlock label="A reprogramar" value={reprogramar} tone="amber" icon={CalendarDays} />
        <KpiBlock label="No avanza / canceladas" value={noAvanza} tone="rose" icon={CalendarDays} />
      </div>
      <div className="text-[10px] text-slate-500 font-mono -mt-2 px-1">
        Reuniones totales (Realizadas + Calendarizadas + Pide cotización): <span className="font-semibold text-slate-700">{realizadas + calendarizadas + pideCoti}</span>
      </div>

      {/* PRÓXIMAS REUNIONES */}
      <section className="bg-gradient-to-b from-sky-50 to-white border border-sky-200 rounded-md p-5" data-testid="bd-reu-proximas">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-sm font-semibold text-slate-900 flex items-center gap-1.5"><Clock className="h-4 w-4 text-sky-600" /> Próximas reuniones</h3>
          <span className="text-[10px] font-mono text-slate-500">{proximas.length} próximas</span>
        </div>
        {proximas.length === 0 ? (
          <p className="text-xs text-slate-400 italic">Sin reuniones próximas en agenda.</p>
        ) : (
          <div className="overflow-x-auto -mx-5">
            <table className="w-full text-xs">
              <thead className="bg-sky-100/50 border-y border-sky-200">
                <tr className="text-left text-slate-600 uppercase tracking-wider text-[10px]">
                  <th className="px-3 py-2 font-semibold">Fecha</th>
                  <th className="px-2 py-2 font-semibold">Empresa</th>
                  <th className="px-2 py-2 font-semibold">Contacto</th>
                  <th className="px-2 py-2 font-semibold">Cargo</th>
                  <th className="px-2 py-2 font-semibold">País</th>
                  <th className="px-2 py-2 font-semibold">Estado</th>
                  <th className="px-2 py-2 font-semibold">Notas</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-sky-100">
                {proximas.map((b, i) => {
                  const hasDoc = !!findDocBrief(b.Empresa);
                  return (
                    <tr key={i} className="hover:bg-sky-50/40 align-top">
                      <td className="px-3 py-2 font-mono text-[10px] text-slate-700 font-semibold whitespace-nowrap">{b["Fecha Final"] || "—"}</td>
                      <td className="px-2 py-2 font-semibold text-slate-900">
                        <button onClick={() => openBrief(b.Empresa)} className={`hover:underline text-left ${hasDoc ? "text-indigo-700" : ""}`} data-testid={`bd-brief-link-${i}`}>
                          {b.Empresa}{hasDoc && <FileText className="inline h-3 w-3 ml-1 text-indigo-500" />}
                        </button>
                        {b.Web && <a href={b.Web} target="_blank" rel="noreferrer" className="ml-1 text-[10px] text-indigo-600 hover:underline">↗</a>}
                      </td>
                      <td className="px-2 py-2 text-slate-700">{b.Participante || "—"}</td>
                      <td className="px-2 py-2 text-slate-500 text-[11px]">{b.Posicion || "—"}</td>
                      <td className="px-2 py-2 text-slate-500 text-[11px]">{b.Pais || "—"}</td>
                      <td className="px-2 py-2"><StatePill state={b.ESTADO} /></td>
                      <td className="px-2 py-2 text-slate-700 text-[11px] max-w-xs">{b.Comentarios || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Tabla detalle */}
      <section className="bg-white border border-slate-200 rounded-md p-5">
        <div className="flex items-start gap-3 mb-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <h3 className="font-display text-sm font-semibold text-slate-900">Briefs · todas las reuniones</h3>
            <p className="text-[11px] text-slate-500 mt-0.5">{filtered.length} de {briefs.length} · cliente, contacto, fecha, brief y comentarios</p>
          </div>
          <select value={mesFilter} onChange={(e) => setMesFilter(e.target.value)} className="h-9 text-xs border border-slate-200 rounded-md px-2 bg-white" data-testid="bd-reuniones-mes">
            <option value="Todos">Todos los meses</option>
            {monthOptions.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
          </select>
          <select value={stateFilter} onChange={(e) => setStateFilter(e.target.value)} className="h-9 text-xs border border-slate-200 rounded-md px-2 bg-white" data-testid="bd-reuniones-state">
            <option value="Todos">Todos los estados</option>
            {states.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <Input placeholder="Buscar empresa, contacto, brief…" value={search} onChange={(e) => setSearch(e.target.value)} className="h-9 pl-9 bg-white text-xs" data-testid="bd-reuniones-search" />
          </div>
        </div>
        <div className="overflow-x-auto -mx-5">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-y border-slate-200">
              <tr className="text-left text-slate-500 uppercase tracking-wider text-[10px]">
                <th className="px-3 py-2 font-semibold">Fecha</th>
                <th className="px-2 py-2 font-semibold">Empresa</th>
                <th className="px-2 py-2 font-semibold">Contacto</th>
                <th className="px-2 py-2 font-semibold">Cargo</th>
                <th className="px-2 py-2 font-semibold">País</th>
                <th className="px-2 py-2 font-semibold">Industria</th>
                <th className="px-2 py-2 font-semibold">Origen</th>
                <th className="px-2 py-2 font-semibold">Estado</th>
                <th className="px-2 py-2 font-semibold">Comentarios / brief</th>
                <th className="px-2 py-2 font-semibold">Stage pipeline</th>
                <th className="px-2 py-2 font-semibold">Comercial</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((b, i) => {
                const hasDoc = !!findDocBrief(b.Empresa);
                return (
                  <tr key={i} className="hover:bg-slate-50 align-top">
                    <td className="px-3 py-2 font-mono text-[10px] text-slate-600 whitespace-nowrap">
                      <div>{b["Fecha Final"] || "—"}</div>
                      {b._mesIdx >= 0 && b._year && (
                        <div className="text-[9.5px] text-slate-400 uppercase tracking-wider">{MESES_SHORT[b._mesIdx]} &apos;{String(b._year).slice(-2)}</div>
                      )}
                    </td>
                    <td className="px-2 py-2 font-medium text-slate-800">
                      <button onClick={() => openBrief(b.Empresa)} className={`hover:underline text-left ${hasDoc ? "text-indigo-700 font-semibold" : ""}`} data-testid={`bd-brief-row-link-${i}`}>
                        {b.Empresa}{hasDoc && <FileText className="inline h-3 w-3 ml-1 text-indigo-500" />}
                      </button>
                      {b.Web && <a href={b.Web} target="_blank" rel="noreferrer" className="ml-1 text-[10px] text-indigo-600 hover:underline">↗</a>}
                    </td>
                    <td className="px-2 py-2 text-slate-700">{b.Participante || "—"}</td>
                    <td className="px-2 py-2 text-slate-500 text-[11px]">{b.Posicion || "—"}</td>
                    <td className="px-2 py-2 text-slate-500 text-[11px]">{b.Pais || "—"}</td>
                    <td className="px-2 py-2 text-slate-500 text-[11px]">{b.Industria || "—"}</td>
                    <td className="px-2 py-2"><span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded-sm ${b._origen === "inbound" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-violet-50 text-violet-700 border border-violet-200"}`}>{b._origen === "inbound" ? "In" : "Out"}</span></td>
                    <td className="px-2 py-2"><StatePill state={b.ESTADO} /></td>
                    <td className="px-2 py-2 text-slate-700 text-[11px] max-w-xs">{b.Comentarios || "—"}</td>
                    <td className="px-2 py-2 text-slate-500 text-[10px] italic">{b["STAGE PIPELINE"] || "—"}</td>
                    <td className="px-2 py-2 text-slate-500 text-[11px]">{b.Comercial || "—"}</td>
                  </tr>
                );
              })}
              {filtered.length === 0 && <tr><td colSpan={11} className="text-center text-[11px] text-slate-400 italic py-6">Sin reuniones que coincidan con el filtro.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>
      {/* Brief Drawer */}
      <BriefDrawer
        open={!!openedBrief}
        onClose={() => setOpenedBrief(null)}
        empresa={openedBrief?.empresa}
        doc={openedBrief?.doc}
        sheetRow={openedBrief?.sheetRow}
        docConfigured={docQ.data?.configured}
      />
    </div>
  );
}

function BriefDrawer({ open, onClose, empresa, doc, sheetRow, docConfigured }) {
  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="bd-brief-drawer">
        <SheetHeader>
          <SheetTitle className="font-display text-base flex items-center gap-2 leading-tight">
            <Briefcase className="h-5 w-5 text-indigo-600 shrink-0" />
            <span className="break-words">{doc?.nombre_empresa || doc?.empresa_raw || empresa}</span>
          </SheetTitle>
          {sheetRow && (
            <SheetDescription className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] mt-1">
              {sheetRow["Fecha Final"] && <span className="font-mono"><Clock className="inline h-3 w-3 mr-1" />{sheetRow["Fecha Final"]}</span>}
              {sheetRow.ESTADO && <StatePill state={sheetRow.ESTADO} />}
              <span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded-sm ${sheetRow._origen === "inbound" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-violet-50 text-violet-700 border border-violet-200"}`}>{sheetRow._origen === "inbound" ? "Inbound" : "Outbound"}</span>
            </SheetDescription>
          )}
        </SheetHeader>

        <div className="mt-5 space-y-4 text-sm">
          {!doc && (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-900">
              {!docConfigured ? (
                <>No hay <span className="font-semibold">Doc de briefs</span> configurado. Pegá el link del Google Doc en <span className="font-semibold">Configurar compromiso</span> → URL del Google Doc.</>
              ) : (
                <>No encontré un brief en el Doc que coincida con <span className="font-semibold">«{empresa}»</span>. Verificá que el nombre en el Sheet coincida con el heading del Doc (sin tildes y mayúsculas no importan).</>
              )}
            </div>
          )}

          {doc && (
            <>
              {/* Meta */}
              <section className="bg-slate-50 border border-slate-200 rounded-md p-3 text-xs space-y-1.5" data-testid="bd-brief-meta">
                {doc.fecha_hora && <Field icon={Clock} label="Fecha y hora" value={doc.fecha_hora} />}
                {doc.link_meet && (
                  <Field icon={Video} label="Link a reunión" value={
                    <a href={doc.link_meet} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline break-all">{doc.link_meet}</a>
                  } />
                )}
                {doc.industria && <Field icon={Briefcase} label="Industria" value={doc.industria} />}
                {doc.empleados && <Field icon={Users} label="Empleados" value={doc.empleados} />}
                {doc.ubicacion && <Field icon={MapPin} label="Ubicación" value={doc.ubicacion} />}
                {doc.web && (
                  <Field icon={Globe2} label="Web" value={
                    <a href={doc.web} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline break-all">{doc.web}</a>
                  } />
                )}
                {doc.linkedin && (
                  <Field icon={Linkedin} label="LinkedIn empresa" value={
                    <a href={doc.linkedin} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline break-all">{doc.linkedin}</a>
                  } />
                )}
                {doc.proceso && <Field icon={FileText} label="Proceso / Base" value={doc.proceso} />}
              </section>

              {/* Contacto */}
              {(doc.persona || doc.correo || doc.numero) && (
                <section className="bg-white border border-slate-200 rounded-md p-3 text-xs space-y-1.5" data-testid="bd-brief-contacto">
                  <h4 className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Contacto</h4>
                  {doc.persona && <Field icon={Users} label="Persona y puesto" value={doc.persona} />}
                  {doc.correo && (
                    <Field icon={Mail} label="Correo" value={
                      <a href={`mailto:${doc.correo}`} className="text-indigo-600 hover:underline break-all">{doc.correo}</a>
                    } />
                  )}
                  {doc.numero && <Field icon={Phone} label="Número" value={doc.numero} />}
                  {doc.linkedin_persona && (
                    <Field icon={Linkedin} label="LinkedIn persona" value={
                      <a href={doc.linkedin_persona} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline break-all">{doc.linkedin_persona}</a>
                    } />
                  )}
                </section>
              )}

              {/* Observación */}
              {doc.observacion && (
                <section className="bg-amber-50/50 border-l-2 border-amber-300 px-4 py-3 rounded-r-md" data-testid="bd-brief-observacion">
                  <h4 className="text-[10px] uppercase tracking-wider text-amber-800 font-semibold mb-1.5">Observación</h4>
                  <p className="text-xs text-slate-800 leading-relaxed whitespace-pre-line">{doc.observacion}</p>
                </section>
              )}

              {/* Descripción */}
              {doc.descripcion && (
                <section className="bg-white border border-slate-200 rounded-md p-4" data-testid="bd-brief-descripcion">
                  <h4 className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Descripción</h4>
                  <p className="text-xs text-slate-700 leading-relaxed whitespace-pre-line">{doc.descripcion}</p>
                </section>
              )}

              {/* Comentarios del Sheet */}
              {sheetRow?.Comentarios && (
                <section className="bg-slate-50 border border-slate-200 rounded-md p-3 text-xs">
                  <h4 className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Notas internas (Sheet)</h4>
                  <p className="text-xs text-slate-700 leading-relaxed whitespace-pre-line">{sheetRow.Comentarios}</p>
                </section>
              )}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Field({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="h-3 w-3 text-slate-400 mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mr-1.5">{label}:</span>
        <span className="text-slate-800">{value}</span>
      </div>
    </div>
  );
}

function PsRow({ label, value, tone }) {
  return (
    <tr className="border-b border-lime-300/60 last:border-0">
      <td className="px-3 py-2 bg-lime-200/70 font-bold text-slate-900 text-xs w-2/3">{label}</td>
      <td className={`px-3 py-2 text-xs font-semibold tabular-nums font-mono text-right border-l border-lime-300 ${tone || "text-slate-900"}`}>{value}</td>
    </tr>
  );
}

function ProjectStatusPanel({ ps }) {
  if (!ps.hasConfig) {
    return (
      <section className="bg-amber-50/60 border border-amber-200 border-dashed rounded-md p-4 text-xs text-amber-900" data-testid="bd-project-status-empty">
        <span className="font-semibold">Sin plan del proyecto cargado.</span> Definí <span className="font-medium">Kick Off / Inicio Outbound / Finalización / Objetivo de reuniones</span> en <span className="font-medium">Configurar compromiso</span> para activar el panel y el semáforo.
      </section>
    );
  }
  const toneBg = {
    emerald: "bg-emerald-600 text-white",
    lime: "bg-lime-500 text-slate-900",
    amber: "bg-amber-500 text-slate-900",
    rose: "bg-rose-600 text-white",
    slate: "bg-slate-300 text-slate-700",
  }[ps.semaforo.tone];

  return (
    <section className="bg-white border border-slate-200 rounded-md p-5" data-testid="bd-project-status">
      <h3 className="font-display text-sm font-semibold text-slate-900 mb-3 flex items-center gap-1.5"><Activity className="h-4 w-4 text-lime-600" /> Estado del proyecto · Reuniones</h3>
      <div className="overflow-hidden rounded-sm border border-lime-300">
        <table className="w-full">
          <tbody>
            <PsRow label="Fecha de Kick Off" value={ps.kickOff || "—"} />
            <PsRow label="Fecha Inicio Outbound" value={ps.inicioOutbound || "—"} />
            <PsRow label="Fecha de finalización" value={ps.finalizacion || "—"} />
            <PsRow label="Meses adicionales" value={ps.mesesAdic} />
            <PsRow label="Objetivo de reuniones" value={ps.objetivo} />
            <PsRow label="Reuniones reprogramadas" value={ps.reprogramadas} tone="text-rose-600 font-bold" />
            <PsRow label="Reuniones alcanzadas" value={ps.alcanzadas} />
            <PsRow label="Pedidos de Cotización" value={ps.pedidosCotizacion} tone={ps.pedidosCotizacion > 0 ? "text-emerald-600 font-bold" : "text-slate-900"} />
            <PsRow label="Reuniones por cerrar" value={ps.porCerrar} />
            <PsRow label="Días Restantes (Hábiles)" value={ps.diasRestantes} />
            <PsRow label="Oportunidades ganadas" value={ps.ganadas} />
            <tr>
              <td className="px-3 py-2 bg-lime-200/70 font-bold text-slate-900 text-xs">SEMÁFORO DEL PROYECTO</td>
              <td className={`px-3 py-2 text-xs font-bold text-center border-l border-lime-300 ${toneBg}`}>{ps.semaforo.label}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );
}

/* ============== Reporte semanal ============== */
function ReporteSemanalSection({ clientId, hasSheet, cfg = {}, hasOutreach = false, hasPorFuera = false }) {
  const briefsQ = useQuery({ queryKey: ["bd-briefs", clientId], queryFn: () => fetchBdBriefs(clientId), enabled: hasSheet });
  const summaryQ = useQuery({ queryKey: ["bd-summary", clientId], queryFn: () => fetchBdSummary(clientId), enabled: hasSheet });
  const briefs = briefsQ.data?.briefs || [];
  const ac = summaryQ.data?.acumulado || {};
  const acciones = summaryQ.data?.acciones_semanales || [];
  // Dual-profile support (Werelo has Jose + Lucilliana). When present, we
  // render one KPI/Actions block per profile side-by-side.
  const perfiles = summaryQ.data?.perfiles || [];
  const kpisPorPerfil = summaryQ.data?.kpis_por_perfil || {};
  const accionesPorPerfil = summaryQ.data?.acciones_por_perfil || {};
  const hasDualProfile = perfiles.length > 1;

  const ALCANZADA_RE = /realizada|calendarizad|pide\s*cotiz|cotizaci|meet\s*done|meeting\s*done|scheduled|quotation\s*request|quote\s*request/i;
  const alcanzadas = useMemo(() => briefs.filter((b) => ALCANZADA_RE.test(b.ESTADO || "")), [briefs]);
  const reprogramadas = useMemo(() => briefs.filter((b) => /reprogramar|reschedul/i.test(b.ESTADO || "")), [briefs]);
  const porCerrar = useMemo(() => briefs.filter((b) => /esperando|coordinando|por cerrar|confirmation\s*waiting|coordinating/i.test(b.ESTADO || "")), [briefs]);
  const ganadas = useMemo(() => briefs.filter((b) => /ganada|ganado|\bwon\b/i.test((b["STAGE PIPELINE"] || "") + " " + (b.ESTADO || ""))), [briefs]);
  const pedidosCotizacion = useMemo(() => briefs.filter((b) => /pide\s*cotiz|cotizaci|quotation\s*request|quote\s*request/i.test((b.ESTADO || "") + " " + (b["STAGE PIPELINE"] || ""))), [briefs]);

  const byMonth = useMemo(() => {
    const decorated = _assignBriefYears(alcanzadas);
    const counts = new Map();
    decorated.forEach((b) => {
      if (b._mesIdx < 0) return;
      const k = `${b._year}-${b._mesIdx}`;
      const prev = counts.get(k) || { year: b._year, idx: b._mesIdx, value: 0 };
      prev.value += 1;
      counts.set(k, prev);
    });
    return Array.from(counts.values())
      .sort((a, b) => (a.year - b.year) || (a.idx - b.idx))
      .map(({ year, idx, value }) => ({
        mes: MESES_ORDER[idx],
        year,
        label: `${MESES_SHORT[idx]} '${String(year).slice(-2)}`,
        value,
      }));
  }, [alcanzadas]);
  const byCampaign = useMemo(() => {
    const counts = {};
    alcanzadas.forEach((b) => { const c = (b.Campaña || "Sin campaña").trim(); counts[c] = (counts[c] || 0) + 1; });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]).map(([campaign, val]) => ({ campaign, value: val }));
  }, [alcanzadas]);
  const totalCount = byMonth.reduce((acc, x) => acc + x.value, 0);
  const totalCampaignCount = byCampaign.reduce((acc, x) => acc + x.value, 0);

  const projectStatus = useMemo(() => {
    // Prefer values coming from the "Gral Numbers" tab of the Sheet (if present)
    // over hardcoded config — so operators can edit the sheet and see the
    // semáforo/objetivos update without touching code or the compromiso form.
    const gral = summaryQ.data?.gral_numbers || {};
    const parseGralDate = (s) => {
      // Gral Numbers uses DD/MM/YYYY. parseBriefDate already handles that.
      if (!s) return null;
      return parseBriefDate(s);
    };
    const kickOff = parseGralDate(gral.kick_off_date) || parseBriefDate(cfg.kick_off);
    const inicioOutbound = parseGralDate(gral.outbound_start_date) || parseBriefDate(cfg.inicio_outbound);
    const finalizacion = parseGralDate(gral.end_date) || parseBriefDate(cfg.finalizacion);
    const objetivo = Number(gral.meeting_goals ?? cfg.objetivo_reuniones ?? 0);
    const mesesAdic = Number(gral.adicional_month ?? cfg.meses_adicionales ?? 0);
    const today = new Date();
    const businessDays = (from, to) => {
      if (!from || !to) return 0;
      let count = 0;
      const cur = new Date(from); cur.setHours(0, 0, 0, 0);
      const end = new Date(to); end.setHours(0, 0, 0, 0);
      while (cur <= end) { const d = cur.getDay(); if (d !== 0 && d !== 6) count++; cur.setDate(cur.getDate() + 1); }
      return count;
    };
    const totalDays = businessDays(inicioOutbound || kickOff, finalizacion);
    const elapsed = businessDays(inicioOutbound || kickOff, today);
    const remaining = Math.max(0, businessDays(today, finalizacion));
    const reusEsperadas = totalDays > 0 ? (objetivo * elapsed) / totalDays : 0;
    const proyectadas = elapsed > 0 ? Math.round((alcanzadas.length / elapsed) * totalDays) : 0;
    let semaforo = { label: "—", tone: "slate" };
    // Prefer the sheet's own traffic-light text if present (In Progress / OK /
    // At Risk / Critical), else compute from the ratio.
    const gralLight = (gral.traffic_light || "").toString().toLowerCase().trim();
    if (gralLight) {
      if (/muy bien|excellent|on track/.test(gralLight)) semaforo = { label: "Muy bien", tone: "emerald" };
      else if (/bien|in progress|good/.test(gralLight)) semaforo = { label: "En curso", tone: "lime" };
      else if (/riesgo|at risk|warning/.test(gralLight)) semaforo = { label: "Riesgo", tone: "amber" };
      else if (/crítico|critico|critical|off track/.test(gralLight)) semaforo = { label: "Crítico", tone: "rose" };
      else semaforo = { label: gral.traffic_light, tone: "slate" };
    } else if (objetivo > 0 && reusEsperadas > 0) {
      const ratio = alcanzadas.length / reusEsperadas;
      if (ratio >= 1) semaforo = { label: "Muy bien", tone: "emerald" };
      else if (ratio >= 0.8) semaforo = { label: "Bien", tone: "lime" };
      else if (ratio >= 0.6) semaforo = { label: "Riesgo", tone: "amber" };
      else semaforo = { label: "Crítico", tone: "rose" };
    }
    return {
      kickOff: kickOff ? kickOff.toLocaleDateString("es-AR") : null,
      inicioOutbound: inicioOutbound ? inicioOutbound.toLocaleDateString("es-AR") : null,
      finalizacion: finalizacion ? finalizacion.toLocaleDateString("es-AR") : null,
      mesesAdic, objetivo,
      reprogramadas: reprogramadas.length, alcanzadas: alcanzadas.length,
      pedidosCotizacion: pedidosCotizacion.length, porCerrar: porCerrar.length,
      proyectadas, diasRestantes: remaining,
      reusEsperadas: Math.round(reusEsperadas * 10) / 10,
      ganadas: ganadas.length, semaforo,
      hasConfig: !!(kickOff || finalizacion || objetivo),
    };
  }, [cfg, summaryQ.data, alcanzadas, reprogramadas, pedidosCotizacion, porCerrar, ganadas]);

  if (!hasSheet) {
    return hasOutreach ? (
      <div className="space-y-5" data-testid="bd-reporte">
        <ReporteOutreachSection clientId={clientId} hasOutreach hasPorFuera={hasPorFuera} />
        <EmptyMsg msg="Configurá el Google Sheet de BD para ver acumulado, acciones semanales y reuniones." />
      </div>
    ) : (
      <EmptyMsg msg="Configurá el Google Sheet primero." />
    );
  }
  if (briefsQ.isLoading || summaryQ.isLoading) return <div className="h-72 rounded-md skeleton" />;

  return (
    <div className="space-y-5" data-testid="bd-reporte">
      {/* Outreach summaries (charts + tag distribution + Por Fuera) */}
      <ReporteOutreachSection clientId={clientId} hasOutreach={hasOutreach} hasPorFuera={hasPorFuera} />

      {/* Acumulado del ciclo (single profile) OR per-profile KPIs (Werelo dual) */}
      {!hasDualProfile ? (
        <section className="bg-white border border-slate-200 rounded-md p-5">
          <h3 className="font-display text-sm font-semibold text-slate-900 mb-3">Acumulado del ciclo</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2.5">
            {Object.entries(ac).map(([k, v]) => (
              <div key={k} className="bg-slate-50 border border-slate-200 rounded-md p-3">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-0.5">{k}</div>
                <div className="font-mono text-lg font-semibold tabular-nums text-slate-900">{typeof v === "number" ? v.toLocaleString() : v}</div>
              </div>
            ))}
            {Object.keys(ac).length === 0 && <div className="text-xs text-slate-400 italic col-span-full">Sin data en la planilla.</div>}
          </div>
        </section>
      ) : (
        <section className="bg-white border border-slate-200 rounded-md p-5" data-testid="bd-acumulado-dual">
          <h3 className="font-display text-sm font-semibold text-slate-900 mb-3">Acumulado por perfil · última semana</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {perfiles.map((perfil) => {
              const kpis = kpisPorPerfil[perfil] || {};
              return (
                <div key={perfil} className="border border-slate-200 rounded-md p-4 bg-slate-50/50">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="h-2 w-2 rounded-full bg-lime-500" />
                    <div className="text-sm font-semibold text-slate-900">{perfil}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(kpis).map(([k, v]) => (
                      <div key={k} className="bg-white border border-slate-200 rounded-md p-2.5">
                        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-0.5 truncate" title={k}>{k}</div>
                        <div className="font-mono text-base font-semibold tabular-nums text-slate-900">{typeof v === "number" ? v.toLocaleString() : v}</div>
                      </div>
                    ))}
                    {Object.keys(kpis).length === 0 && <div className="text-xs text-slate-400 italic col-span-full">Sin data.</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Acciones semanales (single profile) OR per-profile Actions (dual) */}
      {!hasDualProfile ? (
      <section className="bg-white border border-slate-200 rounded-md p-5">
        <h3 className="font-display text-sm font-semibold text-slate-900 mb-3">Acciones · antepenúltima vs última semana</h3>
        <div className="overflow-x-auto -mx-5">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-y border-slate-200">
              <tr className="text-left text-slate-500 uppercase tracking-wider text-[10px]">
                <th className="px-3 py-2 font-semibold">Acción</th>
                <th className="px-2 py-2 font-semibold text-right">Antepenúltima sem.</th>
                <th className="px-2 py-2 font-semibold text-right">Última sem.</th>
                <th className="px-2 py-2 font-semibold text-right">Δ</th>
                <th className="px-2 py-2 font-semibold text-right">% MoM</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {acciones.map((a, i) => {
                const diff = a.ultima - a.anteultima;
                const pct = a.anteultima > 0 ? Math.round((diff / a.anteultima) * 100) : 0;
                const TrendIcon = diff > 0 ? TrendingUp : diff < 0 ? TrendingDown : Minus;
                return (
                  <tr key={i} className="hover:bg-slate-50">
                    <td className="px-3 py-2 font-medium text-slate-800">{a.accion}</td>
                    <td className="px-2 py-2 text-right font-mono tabular-nums text-slate-600">{a.anteultima.toLocaleString()}</td>
                    <td className="px-2 py-2 text-right font-mono tabular-nums text-slate-900 font-semibold">{a.ultima.toLocaleString()}</td>
                    <td className={`px-2 py-2 text-right font-mono tabular-nums font-semibold ${diff > 0 ? "text-emerald-700" : diff < 0 ? "text-rose-700" : "text-slate-500"}`}>{diff > 0 ? "+" : ""}{diff.toLocaleString()}</td>
                    <td className={`px-2 py-2 text-right font-mono tabular-nums ${diff > 0 ? "text-emerald-700" : diff < 0 ? "text-rose-700" : "text-slate-500"} inline-flex items-center justify-end gap-1`}>
                      <TrendIcon className="h-3 w-3" /> {pct > 0 ? "+" : ""}{pct}%
                    </td>
                  </tr>
                );
              })}
              {acciones.length === 0 && <tr><td colSpan={5} className="text-center text-[11px] text-slate-400 italic py-4">Sin acciones cargadas.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>
      ) : (
      <section className="bg-white border border-slate-200 rounded-md p-5" data-testid="bd-acciones-dual">
        <h3 className="font-display text-sm font-semibold text-slate-900 mb-3">Acciones por perfil · antepenúltima vs última semana</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {perfiles.map((perfil) => {
            const filas = accionesPorPerfil[perfil] || [];
            return (
              <div key={perfil} className="border border-slate-200 rounded-md overflow-hidden">
                <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-lime-500" />
                  <div className="text-xs font-semibold text-slate-900">{perfil}</div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50/50 border-b border-slate-100">
                      <tr className="text-left text-slate-500 uppercase tracking-wider text-[10px]">
                        <th className="px-3 py-2 font-semibold">Acción</th>
                        <th className="px-2 py-2 font-semibold text-right">Ante.</th>
                        <th className="px-2 py-2 font-semibold text-right">Última</th>
                        <th className="px-2 py-2 font-semibold text-right">Δ</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {filas.map((a, i) => {
                        const diff = a.ultima - a.anteultima;
                        const TrendIcon = diff > 0 ? TrendingUp : diff < 0 ? TrendingDown : Minus;
                        return (
                          <tr key={i} className="hover:bg-slate-50">
                            <td className="px-3 py-2 font-medium text-slate-800">{a.accion}</td>
                            <td className="px-2 py-2 text-right font-mono tabular-nums text-slate-600">{a.anteultima.toLocaleString()}</td>
                            <td className="px-2 py-2 text-right font-mono tabular-nums text-slate-900 font-semibold">{a.ultima.toLocaleString()}</td>
                            <td className={`px-2 py-2 text-right font-mono tabular-nums font-semibold ${diff > 0 ? "text-emerald-700" : diff < 0 ? "text-rose-700" : "text-slate-500"} inline-flex items-center justify-end gap-1`}>
                              <TrendIcon className="h-3 w-3" /> {diff > 0 ? "+" : ""}{diff.toLocaleString()}
                            </td>
                          </tr>
                        );
                      })}
                      {filas.length === 0 && <tr><td colSpan={4} className="text-center text-[11px] text-slate-400 italic py-4">Sin acciones cargadas.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      </section>
      )}

      {/* Reuniones esta semana */}
      {(summaryQ.data?.reuniones_semana || []).length > 0 && (
        <section className="bg-white border border-slate-200 rounded-md p-5" data-testid="bd-reporte-reuniones-semana">
          <h3 className="font-display text-sm font-semibold text-slate-900 mb-3 flex items-center gap-1.5"><CalendarDays className="h-4 w-4 text-emerald-600" /> Reuniones esta semana</h3>
          <ul className="space-y-1.5">
            {(summaryQ.data?.reuniones_semana || []).map((r, i) => (
              <li key={i} className="flex items-center justify-between text-xs border-b border-slate-100 pb-1.5 last:border-0">
                <span className="font-medium text-slate-800">{r.empresa}</span>
                <span className="font-mono text-slate-500 text-[11px]">{r.fecha}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Estado del proyecto */}
      <ProjectStatusPanel ps={projectStatus} />

      {/* Reuniones por mes / campaña */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-md p-5">
          <h3 className="font-display text-sm font-semibold text-slate-900 mb-3 flex items-center gap-1.5"><CalendarDays className="h-4 w-4 text-lime-600" /> Reuniones por mes <span className="text-[10px] font-normal text-slate-500 normal-case">· Realizada + Calendarizada + Pide Cotización</span></h3>
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-lime-200/60 text-slate-800">
                <th className="px-3 py-2 text-left font-bold text-[11px] border border-lime-300">Mes</th>
                <th className="px-3 py-2 text-right font-bold text-[11px] border border-lime-300">Reu / Coti</th>
              </tr>
            </thead>
            <tbody>
              {byMonth.map(({ label, year, mes, value }) => (
                <tr key={`${year}-${mes}`} className="hover:bg-slate-50">
                  <td className="px-3 py-2 border border-slate-200 text-slate-800">
                    <span>{mes}</span>
                    <span className="ml-2 font-mono text-[10px] text-slate-500">{`'${String(year).slice(-2)}`}</span>
                  </td>
                  <td className="px-3 py-2 border border-slate-200 text-right font-mono tabular-nums font-semibold text-slate-900">{value}</td>
                </tr>
              ))}
              <tr className="bg-lime-200/60 text-slate-900 font-semibold">
                <td className="px-3 py-2 border border-lime-300">Totales</td>
                <td className="px-3 py-2 border border-lime-300 text-right font-mono tabular-nums">{totalCount}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="bg-white border border-slate-200 rounded-md p-5">
          <h3 className="font-display text-sm font-semibold text-slate-900 mb-3 flex items-center gap-1.5"><FileText className="h-4 w-4 text-lime-600" /> Reuniones por campaña <span className="text-[10px] font-normal text-slate-500 normal-case">· Realizada + Calendarizada + Pide Cotización</span></h3>
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-lime-200/60 text-slate-800">
                <th className="px-3 py-2 text-left font-bold text-[11px] italic border border-lime-300">Campaña</th>
                <th className="px-3 py-2 text-right font-bold text-[11px] border border-lime-300">Meets / Cotizaciones</th>
              </tr>
            </thead>
            <tbody>
              {byCampaign.map(({ campaign, value }) => (
                <tr key={campaign} className="hover:bg-slate-50">
                  <td className="px-3 py-2 border border-slate-200 text-slate-800">{campaign}</td>
                  <td className="px-3 py-2 border border-slate-200 text-right font-mono tabular-nums font-semibold text-slate-900">{value}</td>
                </tr>
              ))}
              <tr className="bg-lime-200/60 text-slate-900 font-semibold">
                <td className="px-3 py-2 border border-lime-300">Suma total</td>
                <td className="px-3 py-2 border border-lime-300 text-right font-mono tabular-nums">{totalCampaignCount}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* Gráfico Reuniones x Mes */}
      {byMonth.length > 0 && (
        <section className="bg-white border border-slate-200 rounded-md p-5">
          <h3 className="font-display text-base font-bold text-slate-900 mb-3">Reuniones X Mes</h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={byMonth} margin={{ top: 10, right: 30, left: 0, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#475569" }} />
                <YAxis tick={{ fontSize: 11, fill: "#475569" }} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ fontSize: "11px", padding: "6px 8px", border: "1px solid #e2e8f0", borderRadius: 4 }}
                  formatter={(v) => [v, "Reuniones"]}
                  labelFormatter={(_l, payload) => {
                    const p = payload?.[0]?.payload;
                    return p ? `${p.mes} ${p.year}` : _l;
                  }}
                />
                <Line type="linear" dataKey="value" stroke="#1d4ed8" strokeWidth={3} dot={{ r: 5, fill: "#1d4ed8" }} activeDot={{ r: 7 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </section>
      )}
    </div>
  );
}

function StatePill({ state }) {
  if (!state) return <span className="text-[10px] text-slate-400">—</span>;
  const s = state.toLowerCase();
  let tone = "bg-slate-100 text-slate-700 border-slate-200";
  if (/realizada/.test(s)) tone = "bg-emerald-50 text-emerald-700 border-emerald-200";
  else if (/calendarizad/.test(s)) tone = "bg-sky-50 text-sky-700 border-sky-200";
  else if (/reprogramar|esperando/.test(s)) tone = "bg-amber-50 text-amber-700 border-amber-200";
  else if (/cancelada|no avanza/.test(s)) tone = "bg-rose-50 text-rose-700 border-rose-200";
  return <span className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${tone} whitespace-nowrap`}>{state}</span>;
}

function MedioPill({ medio }) {
  if (!medio) return <span className="text-[10px] text-slate-400">—</span>;
  const s = medio.toLowerCase();
  let tone = "bg-slate-100 text-slate-700 border-slate-200";
  if (/paid|ads|google ad|cpc/.test(s)) tone = "bg-emerald-50 text-emerald-700 border-emerald-200";
  else if (/organic|seo|orgánico|organico/.test(s)) tone = "bg-sky-50 text-sky-700 border-sky-200";
  else if (/referral|referido/.test(s)) tone = "bg-violet-50 text-violet-700 border-violet-200";
  else if (/direct|directo/.test(s)) tone = "bg-amber-50 text-amber-700 border-amber-200";
  else if (/social|linkedin|instagram|facebook/.test(s)) tone = "bg-indigo-50 text-indigo-700 border-indigo-200";
  else if (/email|mail/.test(s)) tone = "bg-rose-50 text-rose-700 border-rose-200";
  return <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-sm border ${tone} whitespace-nowrap truncate inline-block max-w-full`} title={medio}>{medio}</span>;
}

/* ============== Outbound ============== */
function OutboundSection({ clientId, hasSheet, skyleadAccounts }) {
  const hasAccounts = Array.isArray(skyleadAccounts) && skyleadAccounts.length > 0;
  return <OutboundSkyleadSection clientId={clientId} hasAccounts={hasAccounts} />;
}

/* ============== Inbound ============== */
const LEAD_VALID_STATES = ["no avanza", "contactado", "reunion ok"];
function isLeadValido(lead) {
  const s = (lead.Estado || "").toLowerCase().trim();
  return LEAD_VALID_STATES.some((v) => s === v || s.includes(v));
}

function InboundSection({ clientId, hasSheet }) {
  const briefsQ = useQuery({ queryKey: ["bd-briefs", clientId], queryFn: () => fetchBdBriefs(clientId), enabled: hasSheet });
  const leadsQ = useQuery({ queryKey: ["bd-leads", clientId], queryFn: () => fetchBdInboundLeads(clientId), enabled: hasSheet });

  if (!hasSheet) return <EmptyMsg msg="Configurá el Google Sheet primero." />;
  if (briefsQ.isLoading || leadsQ.isLoading) return <div className="h-72 rounded-md skeleton" />;

  const briefsAll = (briefsQ.data?.briefs || []).filter((b) => b._origen === "inbound");
  const briefsAlcanzadas = briefsAll.filter((b) => /realizada|calendarizad|pide\s*cotiz|cotizaci/i.test(b.ESTADO || ""));
  const briefsRealizadas = briefsAll.filter((b) => /realizada/i.test(b.ESTADO || ""));

  const allLeads = leadsQ.data?.leads || [];
  const leads = allLeads.filter(isLeadValido);

  return (
    <div className="space-y-5" data-testid="bd-inbound">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiBlock label="Total leads inbound" value={leads.length} tone="emerald" icon={ArrowDownToLine} />
        <KpiBlock label="Reuniones inbound" value={briefsAlcanzadas.length} tone="indigo" icon={CalendarDays} />
        <KpiBlock label="Reuniones realizadas" value={briefsRealizadas.length} tone="sky" icon={CalendarDays} />
        <KpiBlock label="Países activos" value={new Set(leads.map((l) => (l.Ubicacion || "").split("/")[0])).size} tone="violet" icon={Globe2} />
      </div>

      <LeadsTable leads={leads} />
    </div>
  );
}

function LeadsTable({ leads }) {
  const [search, setSearch] = useState("");
  const [monthFilter, setMonthFilter] = useState("Todos");
  const [estadoFilter, setEstadoFilter] = useState("Todos");
  const [medioFilter, setMedioFilter] = useState("Todos");
  const topScrollRef = useRef(null);
  const bottomScrollRef = useRef(null);
  const syncingRef = useRef(false);

  useEffect(() => {
    const top = topScrollRef.current;
    const bot = bottomScrollRef.current;
    if (!top || !bot) return;
    const onTop = () => {
      if (syncingRef.current) return;
      syncingRef.current = true;
      bot.scrollLeft = top.scrollLeft;
      syncingRef.current = false;
    };
    const onBot = () => {
      if (syncingRef.current) return;
      syncingRef.current = true;
      top.scrollLeft = bot.scrollLeft;
      syncingRef.current = false;
    };
    top.addEventListener("scroll", onTop);
    bot.addEventListener("scroll", onBot);
    return () => {
      top.removeEventListener("scroll", onTop);
      bot.removeEventListener("scroll", onBot);
    };
  }, []);

  // Map "mes" string ("jun - 2026") → numeric (year, month) for chronological sort
  const monthKeyToNumeric = (s) => {
    if (!s) return [0, 0];
    const t = s.toLowerCase().replace(/[-.]/g, " ");
    const parts = t.split(/\s+/).filter(Boolean);
    const ES = { ene: 1, feb: 2, mar: 3, abr: 4, may: 5, jun: 6, jul: 7, ago: 8, sep: 9, set: 9, oct: 10, nov: 11, dic: 12 };
    const EN = { jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6, jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12 };
    let y = 0, m = 0;
    for (const p of parts) {
      if (/^\d{4}$/.test(p)) y = +p;
      else m = m || ES[p.slice(0, 3)] || EN[p.slice(0, 3)] || 0;
    }
    return [y, m];
  };

  const meses = useMemo(() => {
    const set = Array.from(new Set(leads.map((l) => (l._mes || "").trim()).filter(Boolean)));
    // Sort most recent first (matches the table's natural order)
    return set.sort((a, b) => {
      const [ay, am] = monthKeyToNumeric(a);
      const [by, bm] = monthKeyToNumeric(b);
      return by - ay || bm - am;
    });
  }, [leads]);
  const estados = useMemo(() => Array.from(new Set(leads.map((l) => (l.Estado || "").trim()).filter(Boolean))).sort(), [leads]);
  const medios = useMemo(() => Array.from(new Set(leads.map((l) => (l["Medio Ingreso"] || "").trim()).filter(Boolean))).sort(), [leads]);

  const filtered = useMemo(() => leads.filter((l) => {
    if (monthFilter !== "Todos" && (l._mes || "").trim() !== monthFilter) return false;
    if (estadoFilter !== "Todos" && (l.Estado || "").trim() !== estadoFilter) return false;
    if (medioFilter !== "Todos" && (l["Medio Ingreso"] || "").trim() !== medioFilter) return false;
    if (!search) return true;
    const blob = `${l.Nombre} ${l.Apellido} ${l.Empresa} ${l.Email} ${l.Consulta} ${l.Cargo} ${l.Estado} ${l["Medio Ingreso"] || ""}`.toLowerCase();
    return blob.includes(search.toLowerCase());
  }), [leads, search, monthFilter, estadoFilter, medioFilter]);

  return (
    <section className="bg-white border border-slate-200 rounded-md p-5 min-w-0 max-w-full overflow-hidden">
      <div className="flex items-start gap-3 mb-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <h3 className="font-display text-sm font-semibold text-slate-900">Leads inbound</h3>
          <p className="text-[11px] text-slate-500 mt-0.5">{filtered.length} de {leads.length} leads</p>
        </div>
        <select value={estadoFilter} onChange={(e) => setEstadoFilter(e.target.value)} className="h-9 text-xs border border-slate-200 rounded-md px-2 bg-white" data-testid="bd-leads-estado">
          <option value="Todos">Todos los estados</option>
          {estados.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={medioFilter} onChange={(e) => setMedioFilter(e.target.value)} className="h-9 text-xs border border-slate-200 rounded-md px-2 bg-white" data-testid="bd-leads-medio">
          <option value="Todos">Todos los medios</option>
          {medios.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <select value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)} className="h-9 text-xs border border-slate-200 rounded-md px-2 bg-white" data-testid="bd-leads-month">
          <option value="Todos">Todos los meses</option>
          {meses.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <Input placeholder="Buscar nombre, empresa, email…" value={search} onChange={(e) => setSearch(e.target.value)} className="h-9 pl-9 bg-white text-xs" data-testid="bd-leads-search" />
        </div>
      </div>
      <div ref={topScrollRef} className="overflow-x-scroll -mx-5 mb-1 bd-leads-scrollbar h-[12px]" data-testid="bd-leads-scroll-top">
        <div className="min-w-[1640px] h-[1px]" aria-hidden="true" />
      </div>
      <div ref={bottomScrollRef} className="overflow-x-scroll -mx-5 max-h-[700px] bd-leads-scrollbar" data-testid="bd-leads-scroll-bottom">
        <table className="w-full text-[11px] min-w-[1640px] table-fixed">
          <colgroup>
            <col className="w-[100px]" /><col className="w-[140px]" /><col className="w-[140px]" /><col className="w-[140px]" /><col className="w-[180px]" /><col className="w-[120px]" /><col className="w-[140px]" /><col className="w-[280px]" /><col className="w-[100px]" /><col className="w-[140px]" /><col className="w-[200px]" />
          </colgroup>
          <thead className="bg-slate-50 border-y border-slate-200 sticky top-0">
            <tr className="text-left text-slate-500 uppercase tracking-wider text-[10px] whitespace-nowrap">
              <th className="px-2 py-2 font-semibold">Mes</th>
              <th className="px-2 py-2 font-semibold">Medio</th>
              <th className="px-2 py-2 font-semibold">Nombre y apellido</th>
              <th className="px-2 py-2 font-semibold">Empresa</th>
              <th className="px-2 py-2 font-semibold">Email</th>
              <th className="px-2 py-2 font-semibold">Cargo</th>
              <th className="px-2 py-2 font-semibold">Volumen</th>
              <th className="px-2 py-2 font-semibold">Consulta</th>
              <th className="px-2 py-2 font-semibold">Estado</th>
              <th className="px-2 py-2 font-semibold">Última acción</th>
              <th className="px-2 py-2 font-semibold">Comentario</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.slice(0, 300).map((l, i) => (
              <tr key={i} className="hover:bg-slate-50" data-testid={`bd-lead-row-${i}`}>
                <td className="px-2 py-1.5 font-mono text-[10px] text-slate-600 uppercase tracking-wider truncate" title={l._mes} data-testid={`bd-lead-mes-${i}`}>{l._mes || "—"}</td>
                <td className="px-2 py-1.5 truncate" title={l["Medio Ingreso"]} data-testid={`bd-lead-medio-${i}`}>{l["Medio Ingreso"] ? <MedioPill medio={l["Medio Ingreso"]} /> : <span className="text-slate-400">—</span>}</td>
                <td className="px-2 py-1.5 font-medium text-slate-800 truncate" title={`${l.Nombre || ""} ${l.Apellido || ""}`}>{`${l.Nombre || ""} ${l.Apellido || ""}`.trim() || "—"}</td>
                <td className="px-2 py-1.5 text-slate-700 truncate" title={l.Empresa}>
                  <Building2 className="inline h-3 w-3 mr-1 text-slate-400" />{l.Empresa || "—"}
                </td>
                <td className="px-2 py-1.5 text-slate-500 truncate" title={l.Email}>
                  {l.Email && <a href={`mailto:${l.Email}`} className="inline-flex items-center gap-1 hover:text-indigo-600"><Mail className="h-3 w-3 shrink-0" /><span className="truncate">{l.Email}</span></a>}
                </td>
                <td className="px-2 py-1.5 text-slate-500 truncate" title={l.Cargo}>{l.Cargo || "—"}</td>
                <td className="px-2 py-1.5 text-slate-500 truncate" title={l.Volumen}>{l.Volumen || "—"}</td>
                <td className="px-2 py-1.5 text-slate-700 truncate" title={l.Consulta}>{l.Consulta || "—"}</td>
                <td className="px-2 py-1.5">{l.Estado ? <StatePill state={l.Estado} /> : <span className="text-slate-400">—</span>}</td>
                <td className="px-2 py-1.5 text-slate-500 truncate" title={l["Ultima Accion"] || l["Última Accion"] || l["Última acción"]}>{l["Ultima Accion"] || l["Última Accion"] || l["Última acción"] || "—"}</td>
                <td className="px-2 py-1.5 text-slate-600 truncate" title={l.Comentario}>{l.Comentario || "—"}</td>
              </tr>
            ))}
            {filtered.length > 300 && <tr><td colSpan={11} className="text-center text-[10px] text-slate-400 italic py-3">+ {filtered.length - 300} leads más. Filtrá para ver otros.</td></tr>}
            {filtered.length === 0 && <tr><td colSpan={11} className="text-center text-[11px] text-slate-400 italic py-6">Sin leads.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/* ============== Pipeline shared ============== */
function PipelineSection({ items, title, tone = "violet", empty, testid }) {
  // Group by ESTADO or STAGE PIPELINE
  const byStage = useMemo(() => {
    const stages = {};
    items.forEach((b) => {
      const key = (b["STAGE PIPELINE"] || b.ESTADO || "Sin clasificar").trim();
      if (!stages[key]) stages[key] = [];
      stages[key].push(b);
    });
    return stages;
  }, [items]);
  const total = items.length;

  if (total === 0) return <div className="text-xs text-slate-400 italic" data-testid={testid}>{empty}</div>;

  const toneClasses = {
    violet: "from-violet-50",
    emerald: "from-emerald-50",
  };

  return (
    <section className={`bg-gradient-to-b ${toneClasses[tone]} to-white border border-slate-200 rounded-md p-5`} data-testid={testid}>
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <h3 className="font-display text-sm font-semibold text-slate-900">{title}</h3>
        <span className="text-[10px] font-mono text-slate-500">{total} oportunidades</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {Object.entries(byStage).sort((a, b) => b[1].length - a[1].length).map(([stage, list]) => (
          <div key={stage} className="bg-white border border-slate-200 rounded-md p-3">
            <div className="flex items-center justify-between mb-2 pb-2 border-b border-slate-100">
              <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold truncate" title={stage}>{stage}</span>
              <span className="text-[10px] font-mono font-semibold text-slate-900">{list.length}</span>
            </div>
            <ul className="space-y-1.5 max-h-48 overflow-y-auto">
              {list.map((b, i) => (
                <li key={i} className="text-[11px] text-slate-700 leading-tight border-b border-slate-50 pb-1 last:border-0">
                  <div className="font-medium text-slate-900 truncate" title={b.Empresa}>{b.Empresa}</div>
                  <div className="text-[10px] text-slate-500 truncate">{b.Participante || ""}{b.Pais ? ` · ${b.Pais}` : ""}</div>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ============== UI ============== */
function KpiBlock({ label, value, tone = "slate", icon: Icon }) {
  const tones = {
    indigo: "from-indigo-50 to-white border-indigo-200 text-indigo-700",
    sky: "from-sky-50 to-white border-sky-200 text-sky-700",
    emerald: "from-emerald-50 to-white border-emerald-200 text-emerald-700",
    violet: "from-violet-50 to-white border-violet-200 text-violet-700",
    amber: "from-amber-50 to-white border-amber-200 text-amber-700",
    rose: "from-rose-50 to-white border-rose-200 text-rose-700",
    slate: "from-slate-50 to-white border-slate-200 text-slate-700",
  };
  return (
    <div className={`bg-gradient-to-b ${tones[tone]} border rounded-md p-4`}>
      <div className="flex items-center gap-1.5 mb-1">
        {Icon && <Icon className="h-3.5 w-3.5" />}
        <div className="text-[9px] uppercase tracking-wider opacity-90 font-semibold">{label}</div>
      </div>
      <div className="font-mono text-xl font-semibold tabular-nums">{value.toLocaleString()}</div>
    </div>
  );
}

function EmptyMsg({ msg }) {
  return (
    <div className="bg-slate-50 border border-slate-200 border-dashed rounded-md p-8 text-center">
      <Briefcase className="h-8 w-8 text-slate-300 mx-auto mb-2" />
      <p className="text-xs text-slate-500">{msg}</p>
    </div>
  );
}
